import { Chip, Stack, Typography } from "@mui/material";
import { cyan, purple } from "@mui/material/colors";
import { contactReportRoleToLabel } from "encamp-shared/src/utils/contactReportRole";
import { ContactReportRole } from "generated-graphql/graphql";

export type ContactRoleProgramArea = "EPCRA" | "RCRA" | "GENERAL";

export function getContactRoleProgramArea(
  role: ContactReportRole | string
): ContactRoleProgramArea {
  const upper = role.toString().toUpperCase();
  if (upper.startsWith("EPCRA")) return "EPCRA";
  if (upper.startsWith("RCRA")) return "RCRA";
  return "GENERAL";
}

const PROGRAM_AREA_STYLES: Record<
  ContactRoleProgramArea,
  { backgroundColor: string; color: string }
> = {
  EPCRA: {
    backgroundColor: purple[50],
    color: purple[700],
  },
  RCRA: {
    backgroundColor: cyan[50],
    color: cyan[800],
  },
  GENERAL: {
    backgroundColor: "action.selected",
    color: "text.primary",
  },
};

// Program-qualified role label used on chips. RCRA source labels already carry
// the "RCRA " prefix; EPCRA labels do not, so add an "EPCRA " prefix here to
// disambiguate the program area — the chip color alone is not enough
// (ENG-15920). Exported so grid filters/sort/export labels stay in sync with
// the chip text rather than showing the bare "Owner / Operator".
export function contactRoleChipLabel(role: ContactReportRole | string): string {
  const programArea = getContactRoleProgramArea(role);
  const label = contactReportRoleToLabel(role);
  if (programArea === "EPCRA" && !label.startsWith("EPCRA ")) {
    return `EPCRA ${label}`;
  }
  return label;
}

export function ContactRoleChip({ role }: { role: ContactReportRole | string }) {
  const programArea = getContactRoleProgramArea(role);
  const styles = PROGRAM_AREA_STYLES[programArea];
  const label = contactRoleChipLabel(role);

  return (
    <Chip
      label={label}
      size="small"
      variant="filled"
      sx={{
        backgroundColor: styles.backgroundColor,
        color: styles.color,
        height: 22,
      }}
    />
  );
}

export function ContactRoleChipList({
  roles,
}: {
  roles: Array<ContactReportRole | string> | null | undefined;
}) {
  if (!roles || roles.length === 0) return null;
  const unique = Array.from(new Set(roles.map((r) => r.toString())));
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ py: 0.5 }}>
      {unique.map((role) => (
        <ContactRoleChip key={role} role={role} />
      ))}
    </Stack>
  );
}

function LegendChip({ programArea }: { programArea: ContactRoleProgramArea }) {
  const styles = PROGRAM_AREA_STYLES[programArea];
  return (
    <Chip
      label="-"
      size="small"
      variant="filled"
      sx={{
        backgroundColor: styles.backgroundColor,
        color: styles.color,
        height: 22,
        minWidth: 32,
      }}
    />
  );
}

export function ContactRoleChipLegend() {
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={2}
      alignItems="center"
      sx={{ mt: 1.5 }}
      aria-label="Contact role legend"
    >
      <Stack direction="row" alignItems="center" gap={0.75}>
        <LegendChip programArea="EPCRA" />
        <Typography variant="caption" color="text.secondary">
          Chemicals Program Role
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" gap={0.75}>
        <LegendChip programArea="RCRA" />
        <Typography variant="caption" color="text.secondary">
          Waste Program Role
        </Typography>
      </Stack>
    </Stack>
  );
}
